import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from "../../utils/constant";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, isValid } from "date-fns";

const CustomReport = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [formData, setFormData] = useState({
        modules: [],
        detail: false,
        fromDate: '',
        toDate: ''
    });
    const [startDateObj, setStartDateObj] = useState(null);
    const [endDateObj, setEndDateObj] = useState(null);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(null);
    const [tempEndDate, setTempEndDate] = useState(null);

    // Update startDateObj and endDateObj whenever startDate or endDate change
    useEffect(() => {
        if (startDate) {
            setStartDateObj(new Date(startDate));
        }
        if (endDate) {
            setEndDateObj(new Date(endDate));
        }
    }, [startDate, endDate]);

    // Update tempStartDate and tempEndDate whenever startDate or endDate change
    useEffect(() => {
        if (startDate) {
            setTempStartDate(new Date(startDate));
        }
        if (endDate) {
            setTempEndDate(new Date(endDate));
        }
    }, [startDate, endDate]);

    const handleModuleChange = (module) => {
        setFormData(prev => ({
            ...prev,
            modules: prev.modules.includes(module)
                ? prev.modules.filter(m => m !== module)
                : [...prev.modules, module]
        }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchReportData();
    };

    const fetchReportData = async () => {
        try {
            setLoading(true);
            const modules = formData.modules.join(',');
            const token = localStorage.getItem('session');

            const response = await fetch(
                `${API_URL}/pptxreport/${id}?modules=${modules}&detail=${formData.detail}&from=${formData.fromDate}&to=${formData.toDate}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch report data');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `report-${new Date().toISOString().split('T')[0]}.pptx`;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // console.error removed
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="block-header">
                    <div className="row">
                        <div className="col">
                            <h2>Loading...</h2>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Format the displayed date range
    const formatDateRange = () => {
        if (startDate && endDate) {
            return `Selected date: ${startDate ? format(new Date(startDate), 'MM/dd/yyyy') : ''} to ${endDate ? format(new Date(endDate), 'MM/dd/yyyy') : ''}`;
        } else if (startDate) {
            return `From: ${startDate ? format(new Date(startDate), 'MM/dd/yyyy') : ''}`;
        } else if (endDate) {
            return `Till: ${endDate ? format(new Date(endDate), 'MM/dd/yyyy') : ''}`;
        }
        return "Select date range"; // Default placeholder text
    };

    // Handle applying the selected date range
    const applyDateRange = () => {
        if (tempStartDate) {
            setStartDate(format(tempStartDate, 'yyyy-MM-dd'));
        } else {
            setStartDate('');
        }
        if (tempEndDate) {
            setEndDate(format(tempEndDate, 'yyyy-MM-dd'));
        } else {
            setEndDate('');
        }
        setDatePickerOpen(false);
    };

    // Clear all dates when cancel is clicked
    const cancelDateSelection = () => {
        // Clear all date states when cancel is clicked
        setTempStartDate(null);
        setTempEndDate(null);
        setStartDate('');
        setEndDate('');
        setStartDateObj(null);
        setEndDateObj(null);
        setDatePickerOpen(false);
    };

    return (
        <div className="container-fluid">
            <div className="block-header">
                <div className="row">
                    <div className="col">
                        <h2 className="welcome-text">Custom Report</h2>
                    </div>
                </div>
            </div>

            <div className="row clearfix">
                <div className="col-lg-12">
                    <div className="card shadow-sm">
                        <div className="card-body p-4">
                            <h4 className="welcome-textSmall">Configure Your Report</h4>
                            <form onSubmit={handleSubmit}>
                                <div className="row g-4">
                                    <div className="col-md-4">
                                        <div className="date-picker-container">
                                            <div className="date-range-input">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Select date range"
                                                    value={formatDateRange()}
                                                    onClick={() => setDatePickerOpen(true)}
                                                    readOnly
                                                />
                                                <span className="calendar-icon">
                                                    <i className="fa fa-calendar" aria-hidden="true"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-5">
                                            <div className="detail-toggle-card">
                                                <div className="d-flex align-items-start">
                                                    <div>
                                                        <h6 className="mb-1">Detailed Report</h6>
                                                        <p className="text-muted mb-0">Include comprehensive analysis and findings</p>
                                                    </div>
                                                    <div className="form-check form-switch ml-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input inputFormCheck"
                                                            id="detail"
                                                            name="detail"
                                                            checked={formData.detail}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                </div>

                                {/* Modules Section */}
                                <div className="mt-4">
                                    <label className="form-label h5 mb-3">Select Modules</label>
                                    <div className="row g-3">
                                        {['attack-surface', 'dark-web', 'brand-security'].map(module => (
                                            <div className="col-md-4" key={module}>
                                                <div
                                                    className={`module-card ${formData.modules.includes(module) ? 'active' : ''}`}
                                                    onClick={() => handleModuleChange(module)}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className="form-check d-flex align-items-center" style={{ margin: 0 }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input inputFormCheck"
                                                                checked={formData.modules.includes(module)}
                                                                onChange={() => handleModuleChange(module)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                        <div className="module-icon mx-2">
                                                            <i style={{color:"#08283f"}} className={`fas fa-${module === 'attack-surface' ? 'shield-alt' :
                                                                module === 'dark-web' ? 'globe' : 'trademark'
                                                                }`}></i>
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0">
                                                                {module.split('-').map(word =>
                                                                    word.charAt(0).toUpperCase() + word.slice(1)
                                                                ).join(' ')}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="mt-4 d-grid gap-2 d-md-flex justify-content-md-end">
                                    <button type="submit" className="btn btn-primary btn-lg px-5">
                                        <i className="fas fa-file-export mr-2"></i>
                                        Generate Report
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {datePickerOpen && (
                <div className="date-range-overlay" onClick={() => setDatePickerOpen(false)}>
                    <div className="date-range-picker-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="date-range-picker-header">
                            {tempStartDate && tempEndDate
                                ? `Selected date: ${format(tempStartDate, 'MM/dd/yyyy')} to ${format(tempEndDate, 'MM/dd/yyyy')}`
                                : "Select date range"}
                        </div>
                        <div className="date-range-calendars">
                            <div>
                                <h6>From</h6>
                                <DatePicker
                                    selected={tempStartDate}
                                    onChange={(date) => {
                                        setTempStartDate(date);
                                        if (tempEndDate && date > tempEndDate) {
                                            setTempEndDate(null);
                                        }
                                    }}
                                    selectsStart
                                    startDate={tempStartDate}
                                    endDate={tempEndDate}
                                    inline
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                            </div>
                            <div>
                                <h6>To</h6>
                                <DatePicker
                                    selected={tempEndDate}
                                    onChange={(date) => setTempEndDate(date)}
                                    selectsEnd
                                    startDate={tempStartDate}
                                    endDate={tempEndDate}
                                    minDate={tempStartDate}
                                    inline
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                            </div>
                        </div>
                        <div className="date-range-actions">
                            <button
                                className="date-range-apply"
                                onClick={applyDateRange}
                            >
                                Apply Filter
                            </button>
                            <button
                                className="date-range-cancel"
                                onClick={cancelDateSelection}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomReport; 